/**
 * «Чаты Bitrix»: диалоги открытых линий за период — сколько по каким каналам,
 * как быстро отвечают операторы (рабочие часы 10:00–21:00) и сами диалоги —
 * как звонки: строка полей, под ней текст переписки; лид/сделка — панель справа.
 * Спека `docs/specs/2026-09-24-bitrix-chats-design.md`; числа — те же, что у
 * `prokat-runner chats-report`, из одного модуля на сервере.
 */
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useSearchParams } from "react-router";

import {
  type ChatCounts,
  type ChatOperator,
  type ChatSession,
  type ChatsReport,
  fmtReaction,
  lastDays,
  loadChats,
  type Reaction,
  STATUS,
} from "@/lib/bitrixChats";
import type { ObjectKind } from "@/lib/calls";
import { ObjectLink, ObjectPanel } from "@/pages/CallsList";

const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const input = "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg";
const btn = "h-8 rounded-[var(--radius-ui)] border border-border px-2.5 text-fg-muted hover:bg-bg-hover";

/** Период и канал из адреса: ссылку можно переслать. */
function useChatsParams() {
  const [params, setParams] = useSearchParams();
  const def = lastDays();
  const p = {
    from: params.get("from") || def.from,
    to: params.get("to") || def.to,
    source: params.get("source") || "",
    // Открытая панель лида/сделки: `lead:123`.
    panel: params.get("panel") || "",
  };
  const set = (next: Partial<typeof p>) => {
    const v = { ...p, ...next };
    setParams(Object.fromEntries(Object.entries(v).filter(([, x]) => x)), { replace: true });
  };
  return [p, set] as const;
}

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4"
      data-annot={`плитка «${label}»`}
    >
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
    </div>
  );
}

const REACTION_HINT = "только рабочее время 10:00–21:00 между сообщением клиента и ответом оператора";

/** Реакция: > 30 мин — рыжим, > 2 ч — красным. */
function ReactionCell({ sec }: { sec: number | null }) {
  if (sec == null) return <span className="text-fg-faint">—</span>;
  const tone = sec > 7200 ? "text-err" : sec > 1800 ? "text-warn" : "";
  return <span className={tone}>{fmtReaction(sec)}</span>;
}

const COLUMNS: { head: string; hint: string; cell: (c: ChatCounts & Reaction) => ReactNode }[] = [
  { head: "Диалогов", hint: "сессии открытой линии, созданные за период", cell: (c) => c.sessions },
  { head: "Клиент", hint: "сообщений от клиентов", cell: (c) => c.client },
  { head: "Оператор", hint: "сообщений от сотрудников (без автоответов портала)", cell: (c) => c.operator },
  { head: "С лидом", hint: "диалог привязан к лиду", cell: (c) => c.lead },
  { head: "Со сделкой", hint: "диалог привязан к сделке", cell: (c) => c.deal },
  { head: "Без CRM", hint: "диалог не привязан ни к лиду, ни к сделке", cell: (c) => c.no_crm || <span className="text-fg-faint">0</span> },
  {
    head: "Без ответа",
    hint: "оператор так и не ответил (нет первого ответа)",
    cell: (c) => (c.unanswered ? <span className="text-warn">{c.unanswered}</span> : <span className="text-fg-faint">0</span>),
  },
  { head: "1-я реакция", hint: `в среднем, на первое сообщение клиента в диалоге; ${REACTION_HINT}`, cell: (c) => <ReactionCell sec={c.first_reaction} /> },
  { head: "Реакция", hint: `в среднем, на каждое обращение клиента; ${REACTION_HINT}`, cell: (c) => <ReactionCell sec={c.reaction} /> },
];

function Sources({ r, source, set }: { r: ChatsReport; source: string; set: (s: string) => void }) {
  const cell = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="каналы">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">Канал</th>
              {COLUMNS.map((c) => (
                <th
                  key={c.head}
                  title={c.hint}
                  className="cursor-help whitespace-nowrap px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2"
                >
                  {c.head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.sources.map((s) => (
              <tr
                key={s.source}
                className={`cursor-pointer border-t border-border hover:bg-bg-hover ${s.source === source ? "bg-bg-hover font-medium" : ""}`}
                title={s.source === source ? "Показать все каналы" : "Показать диалоги только этого канала"}
                onClick={() => set(s.source === source ? "" : s.source)}
                data-annot={`канал ${s.source}`}
              >
                <td className="whitespace-nowrap px-4 py-1.5">{s.channel}</td>
                {COLUMNS.map((c) => (
                  <td key={c.head} className={cell}>
                    {c.cell(s)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border-strong font-medium">
            <tr data-annot="итог">
              <td className="px-4 py-2">Итого</td>
              {COLUMNS.map((c) => (
                <td key={c.head} className={cell}>
                  {c.cell(r.totals)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

const WHO = { client: "Клиент", operator: "Оператор", system: "Портал" } as const;

/** Переписка текстом, как расшифровка звонка: «03:02 Клиент: …». У ответа оператора — через сколько. */
function DialogText({ s, system }: { s: ChatSession; system: boolean }) {
  const msgs = s.messages.filter((m) => system || m.author !== "system");
  if (s.messages.length === 0) return <span>Переписки ещё нет — синхронизация подтянет её в течение 10 минут.</span>;
  if (msgs.length === 0) return <span>Только сообщения портала.</span>;
  return (
    <>
      {msgs.map((m) => (
        <div key={m.id}>
          <span className="tabular-nums">{m.date.slice(11, 16)}</span>{" "}
          <span className={m.author === "client" ? "text-fg-muted" : ""}>
            {m.author === "operator" && m.name ? m.name : WHO[m.author]}:
          </span>{" "}
          {m.text || "(файл или пусто)"}
          {m.reaction != null && <span className="ml-1.5 text-[11px] text-fg-muted">· ответ через {fmtReaction(m.reaction)}</span>}
        </div>
      ))}
    </>
  );
}

const SESSION_COLUMNS = ["Создан", "Канал", "Клиент", "Оператор", "Статус", "Лид", "Сделка", "1-я реакция", "Реакция", "Кл / оп"];

/** Диалог — две строки, как звонок: поля по колонкам на тонированной плашке и под ними
 *  на всю ширину текст переписки. Столбцы — по содержимому, остаток забирает последний. */
function Sessions({ list, onOpen }: { list: ChatSession[]; onOpen: (kind: ObjectKind, id: number) => void }) {
  const [system, setSystem] = useState(false);
  const td = "bg-[color-mix(in_oklch,var(--fg)_12%,var(--bg))] px-2.5 py-2 font-medium text-fg whitespace-nowrap";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="диалоги">
      <div className="border-b border-border px-4 py-2 text-[12px] text-fg-muted">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={system} onChange={(e) => setSystem(e.target.checked)} />
          сообщения портала (начало диалога, создан лид, автоответ)
        </label>
      </div>
      {list.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">За этот период диалогов нет.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                {SESSION_COLUMNS.map((c) => (
                  <th key={c} className="whitespace-nowrap px-2.5 py-2 font-medium first:pl-4" title={c.includes("еакция") ? REACTION_HINT : undefined}>
                    {c}
                  </th>
                ))}
                <th className="w-full" aria-hidden />
              </tr>
            </thead>
            {list.map((s) => (
              <tbody key={s.id} className="border-t-[3px] border-border" data-annot={`диалог ${s.id}`}>
                <tr>
                  <td className={`${td} pl-4 tabular-nums`}>
                    {dm(s.date)} {s.date.slice(11, 16)}
                  </td>
                  <td className={td}>{s.channel}</td>
                  <td className={`${td} max-w-[16rem] truncate`}>{s.client_name || <span className="text-fg-faint">—</span>}</td>
                  <td className={td}>{s.operator_name || <span className="text-fg-faint">—</span>}</td>
                  <td className={td}>{s.answered ? STATUS[s.status] ?? s.status : <span className="text-warn">без ответа</span>}</td>
                  <td className={td}>
                    <ObjectLink kind="lead" id={s.crm_type === "lead" ? s.crm_id : null} status={s.crm_status} onOpen={onOpen} />
                  </td>
                  <td className={td}>
                    <ObjectLink kind="deal" id={s.crm_type === "deal" ? s.crm_id : null} status={s.crm_status} onOpen={onOpen} />
                  </td>
                  <td className={`${td} tabular-nums`} title={REACTION_HINT}>
                    <ReactionCell sec={s.first_reaction} />
                  </td>
                  <td className={`${td} tabular-nums`} title={`в среднем по ${s.replies} ответам${s.unreplied ? `; без ответа обращений: ${s.unreplied}` : ""}`}>
                    <ReactionCell sec={s.reaction} />
                    {s.unreplied > 0 && <span className="ml-1 text-warn">+{s.unreplied} без отв.</span>}
                  </td>
                  <td className={`${td} tabular-nums`}>
                    {s.client_msgs} / {s.operator_msgs}
                  </td>
                  <td className={td} aria-hidden />
                </tr>
                <tr>
                  <td colSpan={SESSION_COLUMNS.length + 1} className="bg-bg pb-3 pl-6 pr-4 pt-2 text-[12px] leading-[1.5]" data-annot={`текст диалога ${s.id}`}>
                    <div className="whitespace-pre-wrap border-l-2 border-border pl-3 font-normal text-fg-faint">
                      <DialogText s={s} system={system} />
                    </div>
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}

/** Скорость ответа по операторам: за весь период, все каналы. */
function Operators({ list }: { list: ChatOperator[] }) {
  const cell = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="операторы">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-2 font-medium">Оператор</th>
              <th className="px-2.5 py-2 text-right font-medium">Диалогов</th>
              <th className="cursor-help px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2" title={`на первое сообщение клиента; ${REACTION_HINT}`}>
                1-я реакция
              </th>
              <th className="cursor-help px-2.5 py-2 text-right font-medium underline decoration-dotted underline-offset-2" title={`на каждое обращение; ${REACTION_HINT}`}>
                Реакция
              </th>
              <th className="px-2.5 py-2 text-right font-medium">Ответов</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.name} className="border-t border-border">
                <td className="whitespace-nowrap px-4 py-1.5">{o.name}</td>
                <td className={cell}>{o.sessions}</td>
                <td className={cell}>
                  <ReactionCell sec={o.first_reaction} />
                </td>
                <td className={cell}>
                  <ReactionCell sec={o.reaction} />
                </td>
                <td className={cell}>{o.replies}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function BitrixChatsPage() {
  const [p, set] = useChatsParams();
  const q = useQuery({ queryKey: ["bitrix-chats", p.from, p.to, p.source], queryFn: () => loadChats(p) });
  const r = q.data;
  const [panelKind, panelId] = p.panel.split(":") as [ObjectKind | "", string | undefined];
  const openPanel = (kind: ObjectKind, id: number) => set({ panel: `${kind}:${id}` });
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка чатов">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Чаты Bitrix</h1>
        {r?.synced_at && <span className="text-[12px] text-fg-faint">синхронизация {r.synced_at.slice(11, 16)}, раз в 10 мин</span>}
        <div className="ml-auto flex flex-wrap items-center gap-1.5 text-[12.5px]" data-annot="выбор периода">
          <button type="button" className={btn} onClick={() => set({ ...lastDays(1) })}>
            сегодня
          </button>
          <button type="button" className={btn} onClick={() => set({ ...lastDays(7) })}>
            7 дней
          </button>
          <input type="date" value={p.from} onChange={(e) => e.target.value && set({ from: e.target.value })} className={input} />
          <span className="text-fg-faint">—</span>
          <input type="date" value={p.to} onChange={(e) => e.target.value && set({ to: e.target.value })} className={input} />
        </div>
      </div>
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю диалоги…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить чаты: {String(q.error.message)}</div>
      ) : (
        r && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Tile label="Диалогов" value={r.totals.sessions} hint={`${dm(r.from)} — ${dm(r.to)}`} />
              <Tile label="Сообщений клиентов" value={r.totals.client} />
              <Tile label="Сообщений операторов" value={r.totals.operator} />
              <Tile
                label="Без ответа"
                value={r.totals.unanswered ? <span className="text-warn">{r.totals.unanswered}</span> : 0}
                hint="оператор не ответил ни разу"
              />
              <Tile label="Первая реакция" value={fmtReaction(r.totals.first_reaction)} hint="в среднем, рабочие часы 10–21" />
              <Tile label="Реакция на сообщение" value={fmtReaction(r.totals.reaction)} hint={`в среднем по ${r.totals.replies} ответам`} />
            </div>
            <Sources r={r} source={p.source} set={(source) => set({ source })} />
            <div className="text-[12px] text-fg-muted">Скорость ответа операторов — все каналы за период.</div>
            <Operators list={r.operators} />
            <div className="text-[12px] text-fg-muted">
              {p.source ? `Диалоги канала «${r.sources.find((s) => s.source === p.source)?.channel ?? p.source}»` : "Все диалоги"} —
              новые сверху; по каналу в таблице выше — фильтр; по лиду или сделке — панель со звонками клиента.
            </div>
            <Sessions list={r.sessions} onOpen={openPanel} />
          </>
        )
      )}
      {panelKind && panelId && Number(panelId) > 0 && (
        <ObjectPanel kind={panelKind} id={Number(panelId)} onClose={() => set({ panel: "" })} onOpen={openPanel} />
      )}
    </main>
  );
}
